import { useState, useEffect, useRef } from "react";
import { useMusic } from "@/contexts/MusicContext";
import { AppRole } from "@/types/supabase";

interface UseArtistStreamLimitProps {
  isPlaying: boolean;
  user: any;
  userRole: AppRole | null;
  trackId: string;
  trackArtistId: string;
}

export function useArtistStreamLimit({
  isPlaying,
  user,
  userRole,
  trackId,
  trackArtistId,
}: UseArtistStreamLimitProps) {
  const { setIsPlaying, setCurrentTrack } = useMusic();
  const [artistLimitReached, setArtistLimitReached] = useState(false);
  const [streamDuration, setStreamDuration] = useState(0);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [isSelfStream, setIsSelfStream] = useState(false);
  const playTimer = useRef<NodeJS.Timeout | null>(null);

  // Reset state when track changes
  useEffect(() => {
    console.log("🔄 Artist limit reset for new track");
    setArtistLimitReached(false);
    setStreamDuration(0);
    setShowPreviewDialog(false);
    setIsSelfStream(false);
    if (playTimer.current) {
      clearInterval(playTimer.current);
      playTimer.current = null;
    }
  }, [trackId]);

  // Start/stop timer based on playback for artists (own tracks + other artists' tracks)
  useEffect(() => {
    const isArtist = user && userRole === "artist";
    const isSelf = isArtist && user.id === trackArtistId;
    const isOther = isArtist && user.id !== trackArtistId;

    if ((isSelf || isOther) && isPlaying && !artistLimitReached) {
      console.log("⏱️ Starting artist preview timer", { isSelf, trackId });

      playTimer.current = setInterval(() => {
        setStreamDuration((prev) => {
          const newDuration = prev + 1;

          if (newDuration >= 30) {
            console.log("🛑 Artist 30-second preview limit reached", {
              isSelf,
            });
            clearInterval(playTimer.current!);
            setArtistLimitReached(true);
            setIsSelfStream(!!isSelf);
            setShowPreviewDialog(true);
            setIsPlaying(false);
          }

          return newDuration;
        });
      }, 1000);
    }

    return () => {
      if (playTimer.current) {
        clearInterval(playTimer.current);
        playTimer.current = null;
      }
    };
  }, [
    isPlaying,
    user,
    userRole,
    artistLimitReached,
    trackId,
    trackArtistId,
    setIsPlaying,
    setCurrentTrack,
  ]);

  const dismissPreviewDialog = () => {
    setShowPreviewDialog(false);
    setCurrentTrack(null);
  };

  return {
    artistLimitReached,
    streamDuration,
    showPreviewDialog,
    dismissPreviewDialog,
    isSelfStream,
  };
}
